import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Send, ChevronDown, ChevronUp, Heart, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { getAuthHeaders, isAuthenticated } from '../lib/authHeaders';

const API = `${""}/api`;

const CATEGORIES = [
  { id: 'all', label: 'All Posts', color: 'bg-gray-100 text-gray-700' },
  { id: 'clinical_observations', label: 'Clinical Observations', color: 'bg-teal-100 text-teal-700' },
  { id: 'protocol_discussions', label: 'Protocol Discussions', color: 'bg-blue-100 text-blue-700' },
  { id: 'platform_feedback', label: 'Platform Feedback', color: 'bg-amber-100 text-amber-700' },
  { id: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
];

const CommunityForum = ({ onBack, currentUser, initialPostId }) => {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState('all');
  const [showNewPost, setShowNewPost] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [expandedPost, setExpandedPost] = useState(initialPostId || null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);       // post id whose ⋯ menu is open
  const [editingPost, setEditingPost] = useState(null); // post id being edited inline
  const [editContent, setEditContent] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // post id pending delete confirm
  // Optimistic like state: { [postId]: { liked: bool, count: number } }
  const [likeState, setLikeState] = useState({});

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/community/posts?category=${category}`);
      if (res.data?.success) {
        const fetched = res.data.posts || [];
        setPosts(fetched);
        // Seed like state from server (like_count) and detect whether the current
        // user already liked each post (liked_by contains their email).
        const myKey = (currentUser?.email || '').trim().toLowerCase();
        setLikeState(prev => {
          const next = { ...prev };
          fetched.forEach(p => {
            const likedBy = p.liked_by || [];
            next[p.id] = {
              liked: !!myKey && likedBy.map(x => String(x).toLowerCase()).includes(myKey),
              count: typeof p.like_count === 'number' ? p.like_count : likedBy.length,
            };
          });
          return next;
        });
      }
    } catch {}
  }, [category, currentUser?.email]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // Deep-link support: when opened from a notification (initialPostId), expand
  // that post so the user lands directly on the relevant thread.
  useEffect(() => {
    if (initialPostId) setExpandedPost(initialPostId);
  }, [initialPostId]);

  // A post is owned by the current user when its author_email matches (posts
  // store author_email, not author_id). Admins may also edit/delete any post.
  const ownsPost = (post) => {
    const email = (currentUser?.email || '').trim().toLowerCase();
    const author = (post.author_email || '').trim().toLowerCase();
    return currentUser?.role === 'admin' || (!!email && email === author);
  };

  const handleCreatePost = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/community/posts`, {
        title: newTitle, content: newContent, category: newCategory,
        author_name: currentUser?.full_name || 'Anonymous',
        author_email: currentUser?.email || '',
      });
      setNewTitle(''); setNewContent(''); setShowNewPost(false);
      fetchPosts();
    } catch {} finally { setLoading(false); }
  };

  const handleReply = async (postId) => {
    if (!replyText.trim()) return;
    try {
      // Side-effect: the backend reply endpoint creates an in-app notification
      // for the post author (and any @mentioned users). No extra client call.
      await axios.post(`${API}/community/posts/${postId}/reply`, {
        content: replyText,
        author_name: currentUser?.full_name || 'Anonymous',
        author_email: currentUser?.email || '',
      });
      setReplyText('');
      fetchPosts();
    } catch {}
  };

  // Like / unlike — optimistic count update, reconciled with the server response.
  const handleToggleLike = async (postId) => {
    if (!isAuthenticated()) return; // likes require auth (graceful no-op otherwise)
    const current = likeState[postId] || { liked: false, count: 0 };
    const optimistic = {
      liked: !current.liked,
      count: Math.max(0, current.count + (current.liked ? -1 : 1)),
    };
    setLikeState(prev => ({ ...prev, [postId]: optimistic }));
    try {
      const headers = getAuthHeaders();
      // Side-effect: a like (POST) creates an in-app notification for the post
      // author on the backend, deduped to once per 24h. Unlike (DELETE) does not.
      const res = current.liked
        ? await axios.delete(`${API}/community/posts/${postId}/like`, { headers })
        : await axios.post(`${API}/community/posts/${postId}/like`, {}, { headers });
      if (res.data?.success) {
        setLikeState(prev => ({
          ...prev,
          [postId]: { liked: res.data.liked, count: res.data.like_count },
        }));
      }
    } catch {
      setLikeState(prev => ({ ...prev, [postId]: current })); // revert on failure
    }
  };

  const startEdit = (post) => {
    setEditingPost(post.id);
    setEditContent(post.content || '');
    setMenuOpen(null);
  };

  const handleSaveEdit = async (postId) => {
    if (!editContent.trim()) return;
    try {
      await axios.put(`${API}/community/posts/${postId}`, { content: editContent }, { headers: getAuthHeaders() });
      setEditingPost(null);
      setEditContent('');
      fetchPosts();
    } catch {}
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`, { headers: getAuthHeaders() });
      setConfirmDelete(null);
      setMenuOpen(null);
      fetchPosts();
    } catch {}
  };

  const catColor = (cat) => CATEGORIES.find(c => c.id === cat)?.color || 'bg-gray-100 text-gray-700';
  const catLabel = (cat) => CATEGORIES.find(c => c.id === cat)?.label || cat;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="community-forum">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Practitioner Community</h1>
        </div>
        <Button onClick={() => setShowNewPost(!showNewPost)} data-testid="new-post-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
          {showNewPost ? 'Cancel' : 'New Post'}
        </Button>
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setCategory(cat.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${category === cat.id ? 'ring-2 ring-teal-400 ' + cat.color : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            data-testid={`category-filter-${cat.id}`}>
            {cat.label}
          </button>
        ))}
      </div>

      {/* New post form */}
      {showNewPost && (
        <Card className="mb-6 border-teal-200" data-testid="new-post-form">
          <CardContent className="pt-4 space-y-3">
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Post title..." className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="new-post-title" />
            <textarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="Share your observations, questions, or protocol insights..." rows={4} className="w-full px-3 py-2 border rounded-lg text-sm resize-none" data-testid="new-post-content" />
            <div className="flex items-center gap-3">
              <select value={newCategory} onChange={e => setNewCategory(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="new-post-category">
                {CATEGORIES.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <Button onClick={handleCreatePost} disabled={loading || !newTitle.trim() || !newContent.trim()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="submit-post-btn">
                <Send className="w-3.5 h-3.5 mr-1" /> Post
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts list */}
      <div className="space-y-3">
        {posts.length === 0 && (
          <Card className="text-center py-10">
            <CardContent>
              <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide" className="w-20 h-20 mx-auto mb-3 opacity-60" />
              <p className="text-gray-500 text-sm">No posts yet. Be the first to share!</p>
            </CardContent>
          </Card>
        )}
        {posts.map(post => (
          <Card key={post.id} className="hover:shadow-md transition-shadow" data-testid={`post-${post.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base font-semibold text-gray-900">{post.title}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 font-medium">{post.author || post.author_name || 'Anonymous'}</span>
                    <span className="text-xs text-gray-300">&middot;</span>
                    <span className="text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString()}</span>
                    <Badge variant="secondary" className={`text-[10px] ${catColor(post.category)}`}>{catLabel(post.category)}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleLike(post.id)}
                    disabled={!isAuthenticated()}
                    className={`flex items-center gap-1 text-xs transition-colors ${
                      (likeState[post.id]?.liked) ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'
                    } ${!isAuthenticated() ? 'cursor-not-allowed opacity-60' : ''}`}
                    data-testid={`like-${post.id}`}
                    aria-pressed={!!likeState[post.id]?.liked}
                    title={isAuthenticated() ? 'Like this post' : 'Sign in to like'}
                  >
                    <Heart className={`w-3.5 h-3.5 ${likeState[post.id]?.liked ? 'fill-rose-500' : ''}`} />
                    {likeState[post.id]?.count ?? (post.like_count || 0)}
                  </button>
                  <button onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors" data-testid={`expand-${post.id}`}>
                    <MessageCircle className="w-3.5 h-3.5" /> {post.replies?.length || 0}
                    {expandedPost === post.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {/* Owner ⋯ menu */}
                  {ownsPost(post) && (
                    <div className="relative">
                      <button
                        onClick={() => setMenuOpen(menuOpen === post.id ? null : post.id)}
                        className="flex items-center text-gray-400 hover:text-gray-700 transition-colors p-0.5"
                        data-testid={`post-menu-${post.id}`}
                        aria-label="Post options"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      {menuOpen === post.id && (
                        <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1" data-testid={`post-menu-dropdown-${post.id}`}>
                          <button
                            onClick={() => startEdit(post)}
                            className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                            data-testid={`edit-post-${post.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => { setConfirmDelete(post.id); setMenuOpen(null); }}
                            className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {editingPost === post.id ? (
                /* Inline editor */
                <div className="space-y-2" data-testid={`edit-form-${post.id}`}>
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                    data-testid={`edit-content-${post.id}`}
                  />
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => handleSaveEdit(post.id)} disabled={!editContent.trim()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`save-edit-${post.id}`}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingPost(null); setEditContent(''); }} data-testid={`cancel-edit-${post.id}`}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                    p: ({children}) => <p className="mb-1.5 text-sm">{children}</p>,
                    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                    ul: ({children}) => <ul className="list-disc pl-5 space-y-0.5 my-1">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-5 space-y-0.5 my-1">{children}</ol>,
                    li: ({children}) => <li className="text-sm">{children}</li>,
                    h2: ({children}) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
                    h3: ({children}) => <h4 className="text-sm font-semibold mt-1 mb-0.5">{children}</h4>,
                  }}>{post.content}</ReactMarkdown>
                </div>
              )}

              {/* Inline delete confirmation */}
              {confirmDelete === post.id && (
                <div className="mt-3 flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`delete-confirm-${post.id}`}>
                  <span className="text-xs text-red-700">Delete this post? This cannot be undone.</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setConfirmDelete(null)} data-testid={`delete-cancel-${post.id}`}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => handleDelete(post.id)} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`delete-confirm-btn-${post.id}`}>
                      Delete
                    </Button>
                  </div>
                </div>
              )}

              {/* Replies */}
              {expandedPost === post.id && (
                <div className="mt-4 space-y-2 border-t pt-3">
                  {post.replies?.map(reply => (
                    <div key={reply.id} className="pl-4 border-l-2 border-teal-200 py-2">
                      <p className="text-sm text-gray-700">{reply.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 font-medium">{reply.author || reply.author_name || 'Practitioner'}</span>
                        <span className="text-xs text-gray-300">&middot;</span>
                        <span className="text-xs text-gray-400">{new Date(reply.created_at).toLocaleDateString()}</span>
                        {(reply.likes > 0) && <span className="text-xs text-teal-500">{reply.likes} likes</span>}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Write a reply..." className="flex-1 px-3 py-1.5 border rounded-lg text-sm" data-testid={`reply-input-${post.id}`}
                      onKeyDown={e => e.key === 'Enter' && handleReply(post.id)} />
                    <Button size="sm" onClick={() => handleReply(post.id)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`reply-btn-${post.id}`}>
                      <Send className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CommunityForum;
